import { useCallback, useEffect, useState } from "react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useConsumerAuth } from "@/contexts/ConsumerAuthContext";

/**
 * useFavoriteStores — small hook for marking dispensaries as favorites.
 * Requires sign-in (unlike product favorites which work anonymously).
 * Returns { favoriteSlugs:Set, isFav, toggle, count, refresh }.
 */
export const useFavoriteStores = () => {
  const { user } = useConsumerAuth();
  const [favoriteSlugs, setFavoriteSlugs] = useState(new Set());

  const authHeader = useCallback(() => {
    const t = localStorage.getItem("bud_consumer_token");
    return t ? { Authorization: `Bearer ${t}` } : {};
  }, []);

  const refresh = useCallback(async () => {
    if (!user) {
      setFavoriteSlugs(new Set());
      return;
    }
    try {
      const r = await api.get("/consumer/favorites/stores", { headers: authHeader() });
      const slugs = new Set((r.data?.stores || []).map((s) => s.slug));
      setFavoriteSlugs(slugs);
    } catch (e) {
      // silent — UI just won't show stars
    }
  }, [user, authHeader]);

  useEffect(() => {
    refresh();
  }, [refresh]);

  const isFav = useCallback((slug) => favoriteSlugs.has(slug), [favoriteSlugs]);

  const toggle = useCallback(
    async (slug, displayName = "") => {
      if (!user) {
        toast.info("Sign in to save your favorite store", {
          description: "We'll remember it across devices.",
        });
        return false;
      }
      if (!slug) return false;
      const willAdd = !favoriteSlugs.has(slug);
      // optimistic
      setFavoriteSlugs((prev) => {
        const next = new Set(prev);
        if (willAdd) next.add(slug);
        else next.delete(slug);
        return next;
      });
      try {
        if (willAdd) {
          await api.post(`/consumer/favorites/stores/${slug}`, null, {
            headers: authHeader(),
          });
          toast.success(`★ Added ${displayName || "store"} to favorites`);
        } else {
          await api.delete(`/consumer/favorites/stores/${slug}`, {
            headers: authHeader(),
          });
          toast.success(`Removed ${displayName || "store"} from favorites`);
        }
        return true;
      } catch (e) {
        // revert
        setFavoriteSlugs((prev) => {
          const next = new Set(prev);
          if (willAdd) next.delete(slug);
          else next.add(slug);
          return next;
        });
        toast.error("Could not update — try again");
        return false;
      }
    },
    [user, favoriteSlugs, authHeader]
  );

  return {
    favoriteSlugs,
    isFav,
    toggle,
    count: favoriteSlugs.size,
    refresh,
  };
};
